import { RadioButtonGroup, Select } from '@repo/go-ui';
import { TrajectoryListOpenButton } from '@repo/go-widgets/trajectory';
import { hasValue } from '@repo/utils';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './TrajectorySelectionHeader.module.scss';
import type { TTrajectoryPlanOrFact } from './TrajectoryWidget.entity';
import type { TrajectoryWidgetStore } from './TrajectoryWidget.store';

type Props = {
  store: TrajectoryWidgetStore;
};

export const TrajectorySelectionHeader = observer(function TrajectorySelectionHeader({ store }: Props) {
  const { t } = useTranslation(['widgets']);

  const planFactItems = useMemo(
    () => [
      { label: t('widgets:Trajectory.plan'), value: 'plan' as const },
      { label: t('widgets:Trajectory.fact'), value: 'fact' as const },
    ],
    [t],
  );

  const wellboreSelectOptions = store.wellboreOptions.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const trajectorySelectOptions = store.trajectoryOptions.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  return (
    <div className={styles.header}>
      <div className={styles.left}>
        <Select<number>
          className={styles.select}
          size="s"
          placeholder={t('widgets:Trajectory.wellborePlaceholder')}
          options={wellboreSelectOptions}
          value={store.wellboreId}
          disabled={!hasValue(store.wellId) || wellboreSelectOptions.length === 0}
          onChange={(value) => store.setWellboreId(value ?? null)}
        />
        <Select<number>
          className={styles.select}
          size="s"
          placeholder={t('widgets:Trajectory.trajectoryPlaceholder')}
          options={trajectorySelectOptions}
          value={store.trajectoryId}
          disabled={!hasValue(store.wellboreId) || trajectorySelectOptions.length === 0}
          onChange={(value) => store.setTrajectoryId(value ?? null)}
        />
        <TrajectoryListOpenButton disabled={!hasValue(store.wellboreId) || trajectorySelectOptions.length === 0} />
      </div>
      <div className={styles.right}>
        <RadioButtonGroup<TTrajectoryPlanOrFact>
          size="s"
          value={store.planOrFact}
          items={planFactItems}
          onChange={store.setPlanOrFact}
        />
      </div>
    </div>
  );
});
